import { useState } from 'react';
import { Speaker, LogOut, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { acceptTerms } from '../../services/accountStatus';
import { LEGAL_VERSION } from '../../config/legal';

interface TermsAcceptancePanelProps {
  previousVersion: string | null;
  loadFailed?: boolean;
}

export function TermsAcceptancePanel({ previousVersion, loadFailed = false }: TermsAcceptancePanelProps) {
  const { user, logout } = useAuth();
  const [saving, setSaving] = useState(false);

  const handleAgree = async () => {
    if (!user) return;
    setSaving(true);
    try {
      await acceptTerms(user.uid, LEGAL_VERSION);
    } catch (error) {
      console.error('Accepting the terms failed', error);
      toast.error(error instanceof Error ? `Could not save: ${error.message}` : 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSignOut = () => {
    logout().catch((error) => console.error('Sign out failed', error));
  };

  const intro = previousVersion
    ? "We've updated our Terms of Service and Privacy Policy. Please read them and accept to keep using VoteBeats."
    : "We've published Terms of Service and a Privacy Policy. Please read them and accept to keep using VoteBeats.";

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-light dark:bg-background-dark p-6">
      <div className="w-full max-w-md text-center space-y-4">
        <Speaker className="w-12 h-12 text-primary mx-auto" />
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Terms of Service and Privacy Policy</h1>

        {loadFailed ? (
          <>
            <p role="alert" className="text-gray-600 dark:text-gray-400">
              We couldn&apos;t check your account. Reload the page to try again.
            </p>
            <button type="button" className="btn btn-primary w-full" onClick={() => window.location.reload()}>
              <RefreshCw className="w-5 h-5 mr-2" />
              Reload
            </button>
          </>
        ) : (
          <>
            <p className="text-gray-600 dark:text-gray-400">{intro}</p>
            <p className="text-sm">
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary underline">Terms of Service</a>
              <span className="text-gray-400"> · </span>
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary underline">Privacy Policy</a>
            </p>
            <button type="button" className="btn btn-primary w-full" onClick={handleAgree} disabled={saving}>
              {saving ? 'Saving…' : 'I agree'}
            </button>
          </>
        )}

        <button type="button" className="btn btn-secondary w-full" onClick={handleSignOut}>
          <LogOut className="w-5 h-5 mr-2" />
          Sign out
        </button>
      </div>
    </div>
  );
}
